import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { BlogPost } from "@/integrations/supabase/types/blog-types";
import { FileText, Video, BookOpen, Trash2 } from "lucide-react";

interface BlogPostListProps {
  posts: BlogPost[];
  onRefresh: () => void;
  readOnly?: boolean;
}

export function BlogPostList({ posts, onRefresh, readOnly = false }: BlogPostListProps) {
  const { toast } = useToast();

  const handleDelete = async (id: string) => {
    try {
      // Note: blog_posts table doesn't exist, this would need to be created first
      console.log("Would delete blog post:", id);
      // const { error } = await supabase
      //   .from('blog_posts')
      //   .delete()
      //   .eq('id', id);

      // if (error) throw error;

      toast({
        title: "Success",
        description: "Post deleted successfully",
      });

      onRefresh();
    } catch (error) {
      console.error("Error deleting post:", error);
      toast({
        title: "Error",
        description: "Failed to delete post",
        variant: "destructive",
      });
    }
  };

  const getIcon = (post: BlogPost) => {
    if (post.content_type === "video") return Video;
    if (post.url?.includes(".pdf")) return BookOpen;
    return FileText;
  };

  return (
    <div className="space-y-6">
      {posts.map((post) => {
        const Icon = getIcon(post);
        return (
          <Card key={post.id} className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold mb-2">{post.title}</h2>
                  <p className="text-sm text-gray-500 mb-2">By {post.author}</p>
                  {post.content && (
                    <p className="text-gray-600 mb-4">{post.content}</p>
                  )}
                  {post.content_type === "video" && post.video_url && (
                    <div className="aspect-video mb-4">
                      <iframe
                        src={post.video_url}
                        className="w-full h-full"
                        allowFullScreen
                      />
                    </div>
                  )}
                  {post.url && (
                    <a
                      href={post.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      View {post.content_type === "video" ? "Additional Information" : "Full Content"}
                    </a>
                  )}
                </div>
              </div>
              {!readOnly && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(post.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}